import type { CatalogProduct } from "@/domain/catalog";
import type { BreadcrumbItem } from "@/composites/navigation";

/**
 * PDP-side adapter: builds the breadcrumb chain shown above the gallery.
 * Home → CategoryHierarchyEntry titles (shallowest first, e.g. supercategory
 * before category) → product title (current, no href).
 *
 * Category hrefs use the legacy Hybris pattern and are *cumulative*: each
 * level's href includes every ancestor segment up to and including itself.
 *
 * `homeUrl` and `categoryUrlBase` are env-aware strings supplied by the
 * route (e.g. `https://sandbox.pamperedchef.com` prefix when running
 * locally, empty prefix on deployed envs) so SSR and CSR agree.
 */
export function buildBreadcrumbItems(
	product: CatalogProduct,
	options: { homeUrl: string; categoryUrlBase: string }
): BreadcrumbItem[] {
	const pathSegments: string[] = [];
	const categoryItems: BreadcrumbItem[] = (product.categories ?? []).map(
		(c) => {
			pathSegments.push(formEncodeSegment(c.title));
			return {
				label: c.title,
				href: `${options.categoryUrlBase}/${pathSegments.join("/")}`
			};
		}
	);
	return [
		{ label: "Home", href: options.homeUrl },
		...categoryItems,
		{ label: product.title }
	];
}

/**
 * `application/x-www-form-urlencoded` encoding for a single path segment.
 * Spaces → `+`, other reserved chars → `%XX`. Matches Hybris URLs.
 */
function formEncodeSegment(value: string): string {
	return encodeURIComponent(value).replace(/%20/g, "+");
}
